<script setup lang="ts">
  import {
    Modal,
    UiButton,
    ReorderableDropdown,
    TabContainer,
    type Tab,
  } from '@logistiek-zonder-papier/lzp-library'
  import { computed, reactive, ref } from 'vue'
  import { AvailableHomeWidgets, AvailableMenuWidgets } from '@/types/widgets.ts'
  import { useFlowConfigStore } from '@/store/flow-config.store'
  import { useSubAppColours } from '@/composables/use-sub-app-colours'
  import SubAppPreviewPanel from '@/components/form-wizard/SubAppPreviewPanel.vue'
  import SubAppStyleTab from '@/components/form-wizard/SubAppStyleTab.vue'

  const { isSubmitDisabled, savedCssVariables, save } = defineProps<{
    isSubmitDisabled: boolean
    savedCssVariables: Record<string, string>
    save: () => Promise<void>
  }>()

  const flowConfigStore = useFlowConfigStore()

  const show = defineModel<boolean>({ default: true })
  const homeConfig = defineModel<{ widget: string }[]>('homeConfig', { required: true })
  const menuConfig = defineModel<{ widget: string }[]>('menuConfig', { required: true })
  const cssVariables = defineModel<Record<string, string>>('cssVariables', { required: true })

  const { colours, colourGroups, openGroups, resetColours } = useSubAppColours(
    cssVariables,
    savedCssVariables
  )

  const openFlowGroups = reactive<Record<string, boolean>>({})

  const availableHomeWidgets = computed(() => AvailableHomeWidgets.map(widget => ({ widget })))
  const availableMenuWidgets = computed(() => AvailableMenuWidgets.map(widget => ({ widget })))

  const submitting = ref<boolean>(false)

  const tabs: Tab[] = [
    { key: 'home', label: 'home' },
    { key: 'menu', label: 'menu' },
    { key: 'style', label: 'style' },
  ]
  const activeTab = ref<string>('home')

  async function submit() {
    submitting.value = true
    try {
      await save()
    } finally {
      submitting.value = false
    }
  }

  function closeModal(close: () => void) {
    resetColours()
    close()
  }
</script>

<template>
  <Modal v-model:show="show">
    <template #header>
      <span>Sub App Configuration</span>
    </template>

    <div class="center">
      <TabContainer v-model="activeTab" :tabs="tabs">
        <template #home>
          <ReorderableDropdown
            label="Home Config"
            v-model="homeConfig"
            :data="availableHomeWidgets"
            idKey="widget"
          />
        </template>

        <template #menu>
          <ReorderableDropdown
            label="Menu Config"
            v-model="menuConfig"
            :data="availableMenuWidgets"
            idKey="widget"
          />
        </template>

        <template #style>
          <SubAppStyleTab
            :is-submit-disabled="isSubmitDisabled"
            :saved-css-variables="savedCssVariables"
            :css-variables="cssVariables"
            :colour-groups="colourGroups"
            :open-groups="openGroups"
            :open-flow-groups="openFlowGroups"
            :flows="flowConfigStore.flows"
            :has-flow-colour-changes="flowConfigStore.hasFlowColourChanges"
            :reset-colours="resetColours"
            :reset-flow-colours="flowConfigStore.resetFlowColours"
          />
        </template>
      </TabContainer>

      <div class="divider" />

      <div class="alignment">
        <SubAppPreviewPanel
          :flows="flowConfigStore.flows"
          :homeConfig="homeConfig"
          :menuConfig="menuConfig"
          :active-tab="activeTab"
          :colours="colours"
          @open-menu="() => (activeTab = 'menu')"
          @close-menu="() => (activeTab = 'home')"
        />
      </div>
    </div>
    <template #footer="{ close }">
      <div class="button-row">
        <UiButton @click="closeModal(close)" label="Close" />
        <UiButton
          @click="submit"
          label="Opslaan"
          variant="prominent"
          :disabled="isSubmitDisabled && !flowConfigStore.hasFlowColourChanges"
        />
      </div>
    </template>
  </Modal>
</template>

<style lang="css" scoped>
  .alignment {
    align-self: center;
  }

  :deep(.tab-container) {
    margin-top: 0;
  }

  .center {
    width: 100%;
    display: flex;
    flex-grow: 1;
    justify-content: space-between;
    align-items: start;
    gap: var(--spacing-m);
  }

  .button-row {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    justify-content: space-between;

    :deep(.button) {
      width: fit-content;
    }
  }

  :deep(.tab-container) {
    min-height: 35rem;
  }

  .divider {
    width: 1px;
    align-self: stretch;
    background-color: var(--colours-grey-3);
  }
</style>
